import React, { useEffect, useState } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Modal,
  message,
  Popconfirm,
  Tooltip,
  Typography,
  Alert,
  Image,
  Rate,
  Badge,
} from 'antd'
import {
  SearchOutlined,
  ReloadOutlined,
  ExportOutlined,
  EyeOutlined,
  EditOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  StarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  CrownOutlined,
  FireOutlined,
  DeleteOutlined,
  PlusOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store'
import {
  fetchGardens,
  updateGardenStatus,
  batchUpdateStatus,
  fetchStatistics,
  setFilters,
  setSelectedGardens,
  clearSelected,
} from '@store/slices/gardenSlice'
import { gardenService } from '@services/gardenService'
import dayjs from 'dayjs'

const { Title } = Typography
const { RangePicker } = DatePicker
const { Option } = Select

const GardenManagement: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { gardens, loading, error, filters, pagination, selectedGardens, statistics } = useAppSelector(
    (state) => state.gardens
  )

  const [searchText, setSearchText] = useState('')
  const [exportLoading, setExportLoading] = useState(false)
  const [batchModalVisible, setBatchModalVisible] = useState(false)
  const [batchStatus, setBatchStatus] = useState<'approved' | 'pending' | 'inactive'>('approved')

  useEffect(() => {
    dispatch(fetchGardens(filters))
    dispatch(fetchStatistics(filters))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filters.status, filters.search, filters.page, filters.pageSize])

  // 处理搜索
  const handleSearch = () => {
    const searchFilters = { 
      ...filters, 
      search: searchText,
      page: 1 
    }
    dispatch(setFilters(searchFilters))
    dispatch(fetchGardens(searchFilters))
  }

  // 处理重置
  const handleReset = () => {
    const resetFilters = {
      status: 'all' as const,
      farmerId: '',
      location: '',
      startDate: '',
      endDate: '',
      search: '',
      page: 1,
      pageSize: 10,
    }
    dispatch(setFilters(resetFilters))
    dispatch(fetchGardens(resetFilters))
    setSearchText('')
  }

  // 处理分页
  const handleTableChange = (page: number, pageSize: number) => {
    dispatch(setFilters({ ...filters, page, pageSize }))
    dispatch(fetchGardens({ ...filters, page, pageSize }))
  }

  // 处理状态更新
  const handleStatusUpdate = async (id: string, status: 'approved' | 'pending' | 'inactive') => {
    try {
      await dispatch(updateGardenStatus({ id, status })).unwrap()
      message.success('状态更新成功')
      dispatch(fetchStatistics(filters))
    } catch (error: any) {
      message.error(error || '状态更新失败')
    }
  }

  // 处理删除菜园
  const handleDeleteGarden = async (id: string) => {
    try {
      await gardenService.deleteGarden(id)
      message.success('菜园删除成功')
      dispatch(fetchGardens(filters))
      dispatch(fetchStatistics(filters))
    } catch (error: any) {
      message.error(error || '删除失败')
    }
  }

  // 处理发布菜园
  const handlePublishGarden = async (id: string) => {
    try {
      await dispatch(updateGardenStatus({ id, status: 'approved' })).unwrap()
      message.success('菜园发布成功')
      dispatch(fetchStatistics(filters))
    } catch (error: any) {
      message.error(error || '发布失败')
    }
  }

  // 处理下架菜园
  const handleUnpublishGarden = async (id: string) => {
    try {
      await dispatch(updateGardenStatus({ id, status: 'inactive' })).unwrap()
      message.success('菜园下架成功')
      dispatch(fetchStatistics(filters))
    } catch (error: any) {
      message.error(error || '下架失败')
    }
  }

  // 处理添加菜园
  const handleAddGarden = () => {
    navigate('/gardens/create')
  }

  // 处理批量状态更新
  const handleBatchStatusUpdate = async () => {
    if (selectedGardens.length === 0) {
      message.warning('请选择要更新的菜园')
      return
    }

    try {
      await dispatch(batchUpdateStatus({ ids: selectedGardens, status: batchStatus })).unwrap()
      message.success('批量更新成功')
      setBatchModalVisible(false)
      dispatch(clearSelected())
      dispatch(fetchStatistics(filters))
    } catch (error: any) {
      message.error(error || '批量更新失败')
    }
  }

  // 处理导出
  const handleExport = async (format: 'excel' | 'csv' | 'pdf') => {
    setExportLoading(true)
    try {
      await gardenService.exportGardens(filters, format)
      message.success('已开始下载 CSV 文件')
    } catch (error: any) {
      message.error(error?.message || error || '导出失败')
    } finally {
      setExportLoading(false)
    }
  }

  // 获取状态标签 - 与小程序保持一致
  const getStatusTag = (status: string) => {
    const statusMap = {
      approved: { color: 'green', text: '已发布' },
      pending: { color: 'orange', text: '未发布' },
      inactive: { color: 'red', text: '已下架' },
    }
    const config = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status }
    return <Tag color={config.color}>{config.text}</Tag>
  }

  // 表格列定义
  const columns = [
    {
      title: '菜园信息',
      key: 'gardenInfo',
      width: 250,
      render: (_, record: any) => (
        <Space>
          <Image
            src={record.imageUrl}
            alt={record.name}
            style={{ width: 60, height: 60, borderRadius: 8 }}
            fallback="https://placehold.co/60x60/EEEEEE/999999?text=菜园"
          />
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>
              <Space>
                {record.name}
                {record.isFeatured && <CrownOutlined style={{ color: '#faad14' }} />}
                {record.isAd && <FireOutlined style={{ color: '#ff4d4f' }} />}
              </Space>
            </div>
            <div style={{ fontSize: '12px', color: '#999', marginBottom: 4 }}>
              ID: {record.id}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.description ? `${record.description.substring(0, 30)}...` : '暂无描述'}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: '农户信息',
      key: 'farmerInfo',
      width: 150,
      render: (_, record: any) => (
        <div>
          <div style={{ fontSize: '14px', fontWeight: 500 }}>
            <UserOutlined style={{ marginRight: '4px' }} />
            {record.farmerName}
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            <PhoneOutlined style={{ marginRight: '4px' }} />
            {record.phone}
          </div>
        </div>
      ),
    },
    {
      title: '位置',
      key: 'location',
      width: 120,
      render: (_, record: any) => (
        <div>
          <EnvironmentOutlined style={{ marginRight: '4px' }} />
          {record.location}
        </div>
      ),
    },
    {
      title: '评分',
      key: 'rating',
      width: 100,
      render: (_: any, record: any) => {
        const rating = record.rating || 0
        return (
          <div>
            <Rate disabled value={rating} style={{ fontSize: '14px' }} />
            <div style={{ fontSize: '12px', color: '#999' }}>
              {rating}分
            </div>
          </div>
        )
      },
    },
    {
      title: '订阅情况',
      key: 'subscription',
      width: 120,
      render: (_, record: any) => (
        <div>
          <div style={{ fontSize: '14px', fontWeight: 500 }}>
            {record.currentSubscriptions || 0}/{record.subscriptionCapacity || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            当前/容量
          </div>
        </div>
      ),
    },
    {
      title: '营收统计',
      key: 'revenue',
      width: 120,
      render: (_, record: any) => (
        <div>
          <div style={{ fontSize: '12px', fontWeight: 500 }}>
            ¥{record.totalRevenue || 0}
          </div>
          <div style={{ fontSize: '11px', color: '#999' }}>
            {record.totalOrders || 0}单
          </div>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 120,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      render: (_, record: any) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button 
              type="link" 
              icon={<EyeOutlined />} 
              size="small"
              onClick={() => navigate(`/gardens/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              size="small"
              onClick={() => navigate(`/gardens/edit/${record.id}`)}
            />
          </Tooltip>
          
          {/* 发布/下架按钮 - 与小程序保持一致 */}
          {record.status !== 'approved' && (
            <Popconfirm
              title="确定要发布这个菜园吗？"
              onConfirm={() => handlePublishGarden(record.id)}
            >
              <Tooltip title="发布">
                <Button type="link" icon={<PlayCircleOutlined />} size="small" />
              </Tooltip>
            </Popconfirm>
          )}
          {record.status === 'approved' && (
            <Popconfirm
              title="确定要下架这个菜园吗？"
              onConfirm={() => handleUnpublishGarden(record.id)}
            >
              <Tooltip title="下架">
                <Button type="link" icon={<PauseCircleOutlined />} size="small" danger />
              </Tooltip>
            </Popconfirm>
          )}
          
          {/* 删除按钮 */}
          <Popconfirm
            title="确定要删除这个菜园吗？"
            description="删除后无法恢复，请谨慎操作"
            onConfirm={() => handleDeleteGarden(record.id)}
            okText="删除"
            cancelText="取消"
            okType="danger"
          >
            <Tooltip title="删除">
              <Button type="link" icon={<DeleteOutlined />} size="small" danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>
          菜园管理
        </Title>
        <Typography.Text type="secondary">
          管理所有菜园信息，包括审核、状态控制、数据导出等功能
        </Typography.Text>
      </div>

      <Alert
        type="info"
        showIcon
        closable
        style={{ marginBottom: 24 }}
        message="发布与用户端可见性"
        description="新建菜园会立即出现在本列表（未发布）。只有点击「发布」后，用户端 / 小程序才能看到该菜园；「下架」后用户端不可见，管理端仍可管理。若前后端未共用同一数据库，请确保接口与数据同步一致。"
      />

      {error && (
        <Alert
          message="数据加载失败"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: '24px' }}
        />
      )}

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="总菜园数" value={statistics?.total || 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="已发布" value={statistics?.approved || 0} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="未发布" value={statistics?.pending || 0} valueStyle={{ color: '#fa8c16' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="已下架" value={statistics?.inactive || 0} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="总营收" value={statistics?.totalRevenue || 0} prefix="¥" valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic title="平均评分" value={statistics?.averageRating || '0'} suffix="/5.0" valueStyle={{ color: '#722ed1' }} />
          </Card>
        </Col>
      </Row>

      {/* 筛选和操作区域 */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          {/* 第一行：筛选条件 */}
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="搜索菜园名称、描述或农户"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              value={filters.status}
              onChange={(value) => dispatch(setFilters({ ...filters, status: value }))}
              style={{ width: '100%' }}
            >
              <Option value="all">全部状态</Option>
              <Option value="approved">已发布</Option>
              <Option value="pending">未发布</Option>
              <Option value="inactive">已下架</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              value={filters.location}
              onChange={(value) => dispatch(setFilters({ ...filters, location: value }))}
              style={{ width: '100%' }}
              placeholder="选择地区"
            >
              <Option value="">全部地区</Option>
              <Option value="北京">北京</Option>
              <Option value="上海">上海</Option>
              <Option value="深圳">深圳</Option>
              <Option value="广州">广州</Option>
              <Option value="杭州">杭州</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              value={filters.startDate && filters.endDate ? [dayjs(filters.startDate), dayjs(filters.endDate)] : null}
              onChange={(dates) => {
                if (dates) {
                  dispatch(setFilters({
                    ...filters,
                    startDate: dates[0]?.format('YYYY-MM-DD') || '',
                    endDate: dates[1]?.format('YYYY-MM-DD') || '',
                  }))
                } else {
                  dispatch(setFilters({ ...filters, startDate: '', endDate: '' }))
                }
              }}
              style={{ width: '100%' }}
            />
          </Col>
          
          {/* 第二行：操作按钮 */}
          <Col xs={24}>
            <Space wrap>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                搜索
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
              <Button
                icon={<ExportOutlined />}
                loading={exportLoading}
                onClick={() => handleExport('excel')}
              >
                导出
              </Button>
              {selectedGardens.length > 0 && (
                <Button
                  type="primary"
                  danger
                  onClick={() => setBatchModalVisible(true)}
                >
                  批量操作 ({selectedGardens.length})
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 菜园列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={gardens || []}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination?.page || 1,
            pageSize: pagination?.pageSize || 10,
            total: pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
          }}
          rowSelection={{
            selectedRowKeys: selectedGardens || [],
            onChange: (selectedRowKeys) => {
              dispatch(setSelectedGardens(selectedRowKeys as string[]))
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 悬浮添加按钮 - 与小程序保持一致 */}
      <div style={{
        position: 'fixed',
        right: '24px',
        bottom: '24px',
        zIndex: 1000
      }}>
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<PlusOutlined />}
          onClick={handleAddGarden}
          style={{
            width: '56px',
            height: '56px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}
        />
      </div>

      {/* 批量操作模态框 */}
      <Modal
        title="批量操作"
        open={batchModalVisible}
        onOk={handleBatchStatusUpdate}
        onCancel={() => setBatchModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <p>已选择 {selectedGardens.length} 个菜园</p>
        <p>请选择要更新的状态：</p>
        <Select
          value={batchStatus}
          onChange={setBatchStatus}
          style={{ width: '100%' }}
        >
          <Option value="approved">发布</Option>
          <Option value="pending">设为未发布</Option>
          <Option value="inactive">下架</Option>
        </Select>
      </Modal>
    </div>
  )
}

export default GardenManagement